import { ActionReducer, ActionReducerMap, createSelector, MetaReducer } from "@ngrx/store";
import { authReducer, AuthState } from "app/modules/auth/_state/auth.reducers";
import { AuthorizerReducer, AuthorizerState } from "app/modules/authorizers/_state/authorizers.reducers";
import { BulkPayReducer, BulkPayState } from "app/modules/bulk-pay/_state/bulk-pay.reducers";
import { CustomersReducer, CustomerState } from "app/modules/customers/_state/customers.reducers";
import { DashboardReducer, DashboardState } from "app/modules/dashboard/_state/dashboard.reducers";
import { FeeReducer, FeeState } from "app/modules/fee-sharing/_state/fee-sharing.reducers";
import { NotificationsReducer, NotificationsState } from "app/modules/notifications/_state/notifications.reducers";
import { ProductsReducer, ProductsState } from "app/modules/products/_state/products.reducers";
import { TransactionsReducer, TransactionsState } from "app/modules/transactions/_state/txn.reducers";
import { environment } from "environments/environment";
import { logout } from '../modules/auth/_state/auth.actions';
import { StaffsReducer, StaffState } from "app/modules/fwp/_state/staffs.reducers";
import { PayrollsReducer, PayrollState } from "app/modules/payroll/_state/payroll.reducers";
import { RolesReducer, RoleState } from "app/modules/settings/_state/settings.reducers";
import { BulkTopUpReducer, BulkTopUpState } from "app/modules/bulk-top-up/_state/bulk-top-up.reducers";


export interface AppState {
    auth: AuthState
    products: ProductsState
    transactions: TransactionsState
    userRoles:RoleState
    systemUsers:RoleState
    bulkPay: BulkPayState,
    bulkTopUp: BulkTopUpState,
    authorizers: AuthorizerState
    dashboard: DashboardState

    notifications: NotificationsState
    customers: CustomerState
    fee: FeeState,
    staffs: StaffState,
    payrolls: PayrollState,
}

export const reducers: ActionReducerMap<AppState> = {
    auth: authReducer,
    products: ProductsReducer,
    transactions: TransactionsReducer,
    userRoles:RolesReducer,
    systemUsers:RolesReducer,
    bulkPay: BulkPayReducer,
    bulkTopUp: BulkTopUpReducer,
    authorizers: AuthorizerReducer,
    dashboard: DashboardReducer,
    notifications: NotificationsReducer,
    customers: CustomersReducer,
    fee: FeeReducer,
    staffs: StaffsReducer,
    payrolls: PayrollsReducer
}

export function flush(reducer: ActionReducer<any>): ActionReducer<any> {
    return function(state, action) {
        if (action.type === logout.type) {
            return reducer(undefined, action);
        }
        return reducer(state, action);
    };
}


//MERCHANT PRODUCTS
export const selectProductsState = (state : AppState) => state.products
export const selectMerchantProducts = createSelector(selectProductsState, (state: ProductsState) => state.merchantProducts)
export const selectMerchantProductsLoading = createSelector(selectProductsState, (state: ProductsState) => state.loadingMerchantProducts)
export const selectMerchantProductsError = createSelector(selectProductsState, (state:ProductsState) => state.fetchMerchantProductsError)

//Product Categories
export const selectProductCategories = createSelector(selectProductsState, (state: ProductsState) => state.productCategories)
export const selectProductCategoriesLoading = createSelector(selectProductsState, (state: ProductsState) => state.loadingProductCategories)
export const selectProductCategoriesError = createSelector(selectProductsState, (state: ProductsState) => state.productCategoriesError)

//banks
export const selectBanks = createSelector(selectProductsState, (state: ProductsState) => state.banks)
export const selectBanksLoading = createSelector(selectProductsState, (state: ProductsState) => state.loadingBanks)
export const selectBanksError = createSelector(selectProductsState, (state: ProductsState) => state.banksError)

//Merchant Wallets
export const selectWallets = createSelector(selectProductsState, (state: ProductsState) => state.wallets)
export const selectWalletsLoading = createSelector(selectProductsState, (state: ProductsState) => state.loadingWallet)
export const selectWalletsError = createSelector(selectProductsState, (state: ProductsState) => state.walletsError)

//Merchant Settlement Accounts
export const selectSettlementAccounts = createSelector(selectProductsState, (state: ProductsState) => state.settlementAccount)
export const selectSettlementAccountsLoading = createSelector(selectProductsState, (state: ProductsState) => state.loadingSettlementAccount)
export const selectSettlementAccountsError = createSelector(selectProductsState, (state: ProductsState) => state.settlementAccountError)


//Account Validation
export const selectValidatedAccount = createSelector(selectProductsState, (state: ProductsState) => state.validatedAccount)
export const selectValidatingAccount = createSelector(selectProductsState, (state: ProductsState) => state.validatingAccount)
export const selectValidatingAccountError = createSelector(selectProductsState, (state: ProductsState) => state.validatingAccountError)

//TRANSACTIONS
export const selectTransactionsState = (state : AppState) => state.transactions
export const selectTransactions = createSelector(selectTransactionsState, (state: TransactionsState) => state.transactions)
export const selectTransactionsLoading = createSelector(selectTransactionsState, (state: TransactionsState) => state.loadingTransactions)
export const selectTransactionsError = createSelector(selectTransactionsState, (state: TransactionsState) => state.fetchTransactionsError)

export const selectDebit = createSelector(selectTransactionsState, (state: TransactionsState) => state.debit)
export const selectDebitLoading = createSelector(selectTransactionsState, (state: TransactionsState) => state.debitLoading)
export const selectDebitError = createSelector(selectTransactionsState, (state: TransactionsState) => state.debitError)

export const selectSignaturesResult = createSelector(selectTransactionsState, (state: TransactionsState) => state.signaturesResult)
export const selectSignaturesResultLoading = createSelector(selectTransactionsState, (state: TransactionsState) => state.signaturesResultLoading)
export const selectSignaturesResultError = createSelector(selectTransactionsState, (state: TransactionsState) => state.signaturesResultError)


//BULKPAY
export const selectBulkPayState = (state: AppState) => state.bulkPay
export const selectRecipientCategories = createSelector(selectBulkPayState, (state: BulkPayState) => state.recipientCategories)
export const selectRecipientCategoriesLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.loadingRecipientCategories)
export const selectRecipientCategoriesError = createSelector(selectBulkPayState, (state: BulkPayState) => state.fetchRecipientCategoriesError)

export const selectRecipientBanks = createSelector(selectBulkPayState, (state: BulkPayState) => state.recipientBanks)
export const selectRecipientBanksLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.recipientBanksLoading)
export const selectRecipientBanksError = createSelector(selectBulkPayState, (state: BulkPayState) => state.recipientBanksError)

export const selectRecipients = createSelector(selectBulkPayState, (state: BulkPayState) => state.recipients)
export const selectRecipientsLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.loadingRecipients)
export const selectRecipientsError = createSelector(selectBulkPayState, (state: BulkPayState) => state.fetchRecipientsError)

export const selectXcelAccount = createSelector(selectBulkPayState, (state: BulkPayState) => state.xcelAccount)
export const selectXcelAccountLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.validatingXcelAccount)
export const selectXcelAccountError = createSelector(selectBulkPayState, (state: BulkPayState) => state.validateXcelAccountError)

export const selectCategoryRecipients = createSelector(selectBulkPayState, (state: BulkPayState) => state.categoryRecipients)
export const selectCategoryRecipientsLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.loadingCategoryRecipients)
export const selectCategoryRecipientsError = createSelector(selectBulkPayState, (state: BulkPayState) => state.fetchCategoryRecipientsError)

export const selectAccountBalance = createSelector(selectBulkPayState, (state: BulkPayState) => state.accountBalance)
export const selectAccountBalanceLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.loadingAccountBalance)
export const selectAccountBalanceError = createSelector(selectBulkPayState, (state: BulkPayState) => state.fetchAccountBalanceError)

export const selectCountries = createSelector(selectBulkPayState, (state: BulkPayState) => state.countries)
export const selectCountriesLoading = createSelector(selectBulkPayState, (state: BulkPayState) => state.loadingCountries)
export const selectCountriesError = createSelector(selectBulkPayState, (state: BulkPayState) => state.countriesError)


//BULKTOPUP
export const selectBulkTopUpState = (state: AppState) => state.bulkTopUp
export const selectRecipientCategoriesBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.recipientCategories)
export const selectRecipientCategoriesLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.loadingRecipientCategories)
export const selectRecipientCategoriesErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.fetchRecipientCategoriesError)

export const selectRecipientBanksBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.recipientBanks)
export const selectRecipientBanksLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.recipientBanksLoading)
export const selectRecipientBanksErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.recipientBanksError)

export const selectRecipientsBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.recipients)
export const selectRecipientsLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.loadingRecipients)
export const selectRecipientsErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.fetchRecipientsError)

export const selectXcelAccountBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.xcelAccount)
export const selectXcelAccountLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.validatingXcelAccount)
export const selectXcelAccountErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.validateXcelAccountError)

export const selectCategoryRecipientsBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.categoryRecipients)
export const selectCategoryRecipientsLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.loadingCategoryRecipients)
export const selectCategoryRecipientsErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.fetchCategoryRecipientsError)

export const selectAccountBalanceBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.accountBalance)
export const selectAccountBalanceLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.loadingAccountBalance)
export const selectAccountBalanceErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.fetchAccountBalanceError)

export const selectCountriesBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.countries)
export const selectCountriesLoadingBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.loadingCountries)
export const selectCountriesErrorBTO = createSelector(selectBulkTopUpState, (state: BulkTopUpState) => state.countriesError)

//Authorizers
export const selectAuthorizerState = (state: AppState) => state.authorizers
export const selectMerchantAuthorizers = createSelector(selectAuthorizerState, (state: AuthorizerState) => state.merchantAuthorizers )
export const selectMerchantAuthorizersLoading = createSelector(selectAuthorizerState, (state: AuthorizerState) => state.loadingMerchantAuthorizers)
export const selectMerchantAuthorizersError= createSelector(selectAuthorizerState, (state: AuthorizerState) => state.fetchMerchantAuthorizersError)

//Dashboard
export const selectDashboardState = (state: AppState) => state.dashboard
export const selectOutstandingBalance = createSelector(selectDashboardState, (state: DashboardState) => state.outstandingBalance)
export const selectOutstandingBalanceLoading = createSelector(selectDashboardState, (state: DashboardState) => state.loadingOutstandingBalance)
export const selectOutstandingBalanceError = createSelector(selectDashboardState, (state: DashboardState) => state.outstandingBalanceError)

export const selectTransactionHistory = createSelector(selectDashboardState, (state: DashboardState) => state.transactionHistory)
export const selectTransactionHistoryLoading = createSelector(selectDashboardState, (state: DashboardState) => state.transactionHistoryLoading)
export const selectTransactionHistoryError = createSelector(selectDashboardState, (state: DashboardState) => state.transactionHistoryError)


export const selectPendingTransactions = createSelector(selectDashboardState, (state: DashboardState) => state.pendingTransactions)
export const selectPendingTransactionsLoading = createSelector(selectDashboardState, (state: DashboardState) => state.pendingTransactionsLoading)
export const selectPendingTransactionsError = createSelector(selectDashboardState, (state: DashboardState) => state.pendingTransactionsError)

export const selectPaymentChannels = createSelector(selectDashboardState, (state: DashboardState) => state.paymentChannels)
export const selectPaymentChannelsLoading = createSelector(selectDashboardState, (state: DashboardState) => state.paymentChannelsLoading)
export const selectPaymentChannelsError = createSelector(selectDashboardState, (state: DashboardState) => state.paymentChannelsError)

export const selectChannelProducts = createSelector(selectDashboardState, (state: DashboardState) => state.channelProducts)
export const selectChannelProductsLoading = createSelector(selectDashboardState, (state: DashboardState) => state.channelProductsLoading)
export const selectChannelProductsError = createSelector(selectDashboardState, (state: DashboardState) => state.channelProductsError)

export const selectProductEarning = createSelector(selectDashboardState, (state: DashboardState) => state.productsEarnings)
export const selectProductEarningLoading = createSelector(selectDashboardState, (state: DashboardState) => state.productsEarningsLoading)
export const selectProductEarningError = createSelector(selectDashboardState, (state: DashboardState) => state.productsEarningsError)

export const selectDailyTransactions = createSelector(selectDashboardState, (state: DashboardState) => state.dailyTransactions)
export const selectDailyTransactionsLoading = createSelector(selectDashboardState, (state: DashboardState) => state.dailyTransactionsLoading)
export const selectDailyTransactionsError = createSelector(selectDashboardState, (state: DashboardState) => state.dailyTransactionsError)

export const selectMtbTransactions = createSelector(selectDashboardState, (state: DashboardState) => state.mtbTransactions)
export const selectMtbTransactionsLoading = createSelector(selectDashboardState, (state: DashboardState) => state.mtbTransactionsLoading)
export const selectMtbTransactionsError = createSelector(selectDashboardState, (state: DashboardState) => state.mtbTransactionsError)

//Notifications
export const selectNotificationsState = (state: AppState) => state.notifications
export const selectNotifications = createSelector(selectNotificationsState, (state: NotificationsState) => state.notifications)
export const selectNotificationsLoading = createSelector(selectNotificationsState, (state: NotificationsState) => state.notificationsLoading)
export const selectNotificationsError = createSelector(selectNotificationsState, (state: NotificationsState) => state.notificationsError)

//Customers
export const selectCustomerState = (state: AppState) => state.customers
export const selectServiceTypes = createSelector(selectCustomerState, (state: CustomerState) => state.serviceTypes)
export const selectServiceTypesError = createSelector(selectCustomerState, (state: CustomerState) => state.serviceTypesError)
export const selectServiceTypesLoading = createSelector(selectCustomerState, (state: CustomerState) => state.serviceTypesLoading)

export const selectCustomerTypes = createSelector(selectCustomerState, (state: CustomerState) => state.customerTypes)
export const selectCustomerTypesError = createSelector(selectCustomerState, (state: CustomerState) => state.customerTypesError)
export const selectCustomerTypesLoading = createSelector(selectCustomerState, (state: CustomerState) => state.customerTypesLoading)

export const selectCustomers = createSelector(selectCustomerState, (state: CustomerState) => state.customers)
export const selectCustomersError = createSelector(selectCustomerState, (state: CustomerState) => state.customersError)
export const selectCustomersLoading = createSelector(selectCustomerState, (state: CustomerState) => state.customersLoading)

//Staffs
export const selectStaffState = (state: AppState) => state.staffs
export const selectAllStaffs = createSelector(selectStaffState, (state: StaffState) => state.allStaffs)
export const selectStaffsError = createSelector(selectStaffState, (state: StaffState) => state.staffsError)
export const selectStaffsLoading = createSelector(selectStaffState, (state: StaffState) => state.staffsLoading)

export const selectStaff = createSelector(selectStaffState, (state: StaffState) => state.singleStaff)
export const selectStaffError = createSelector(selectStaffState, (state: StaffState) => state.singleStaffError)
export const selectStaffLoading = createSelector(selectStaffState, (state: StaffState) => state.singleStaffLoading)

// Payroll
export const selectPayrollState = (state: AppState) => state.payrolls
export const selectAllPayrolls = createSelector(selectPayrollState, (state: PayrollState) => state.allPayrolls)
export const selectPayrollsError = createSelector(selectPayrollState, (state: PayrollState) => state.payrollsError)
export const selectPayrollsLoading = createSelector(selectPayrollState, (state: PayrollState) => state.payrollsLoading)

export const selectPayroll = createSelector(selectPayrollState, (state: PayrollState) => state.singlePayroll)
export const selectPayrollError = createSelector(selectPayrollState, (state: PayrollState) => state.singlePayrollError)
export const selectPayrollLoading = createSelector(selectPayrollState, (state: PayrollState) => state.singlePayrollLoading)

//Fee
export const selectFeeState = (state: AppState) => state.fee
export const selectFeeShare = createSelector(selectFeeState , (state: FeeState) => state.feeShare)
export const selectFeeShareError = createSelector(selectFeeState , (state: FeeState) => state.feeShareError)
export const selectFeeShareLoading = createSelector(selectFeeState , (state: FeeState) => state.feeShareLoading)

//Auth
export const selectAuthState = (state: AppState) => state.auth
export const selectEntityTypes = createSelector(selectAuthState, (state: AuthState) => state.entityTypes)
export const selectEntityTypesError = createSelector(selectAuthState, (state: AuthState) => state.entityTypesError)
export const selectEntityTypesLoading = createSelector(selectAuthState, (state: AuthState) => state.entityTypesLoading)

export const selectBusinessTypes = createSelector(selectAuthState, (state: AuthState) => state.businessTypes)
export const selectBusinessTypesError = createSelector(selectAuthState, (state: AuthState) => state.businessTypesError)
export const selectBusinessTypesLoading = createSelector(selectAuthState, (state: AuthState) => state.businessTypesLoading)

export const selectTransactionTypes = createSelector(selectAuthState, (state: AuthState) => state.transactionsTypes)
export const selectTransactionTypesError = createSelector(selectAuthState, (state: AuthState) => state.transactionsTypesError)
export const selectTransactionTypesLoading = createSelector(selectAuthState, (state: AuthState) => state.transactionsTypesLoading)

export const selectCompanyProfile = createSelector(selectAuthState, (state: AuthState) => state.companyProfile)
export const selectCompanyProfileError = createSelector(selectAuthState, (state: AuthState) => state.companyProfileError)
export const selectCompanyProfileLoading = createSelector(selectAuthState, (state: AuthState) => state.companyProfileLoading)

// Users and Roles 
export const selectRolesState = (state: AppState) => state.userRoles
export const selectRoles = createSelector(selectRolesState, (state: RoleState) => state.allUserRoles)
export const selectRolesLaoding = createSelector(selectRolesState, (state: RoleState) => state.rolesLoading)
export const selectRolesError = createSelector(selectRolesState, (state: RoleState) => state.rolesError)



export const selectAllUsersState = (state: AppState) => state.systemUsers;
export const selectSystemUsers = createSelector(selectAllUsersState, (state: RoleState) => state.allSystemUsers);
export const selectSystemUsersLaoding = createSelector(selectAllUsersState, (state: RoleState) => state.usersLoading);
export const selectSystemUsersError = createSelector(selectAllUsersState, (state: RoleState) => state.usersError);






export const metaReducers: MetaReducer<AppState>[] = !environment.production ? [flush] : [flush];